import { useCallback, useEffect } from 'react'
import { useQueries, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query'
import { searchMetadataPage, searchPreviewRecords, type SearchMatchParams } from '../api/search.api'
import { searchQueryKeys } from '../api/queryKeys'
import type {
  SearchEntityHit,
  SearchMetadataKind,
  SearchMetadataPageResponse,
  SearchRecordPageResponse,
} from '../models'
import { RECORD_FETCH_BATCH_ROWS, RECORD_FETCH_DEBOUNCE_MS } from '../logic/constants'
import type { RowRange } from '../logic/globalSearchSections'
import { planMetadataWindow, planRecordBatches } from '../logic/resultWindows'
import { buildMetadataRow, buildRecordRows, isMetadataKind } from '../logic/rows'
import type { Row, Section } from '../logic/types'
import { SEARCH_PAGE_QUERY_OPTIONS, logFailure } from './searchPaging'
import { useScopedState } from './useScopedState'

/** A page of rows a result tab asked for while scrolling. */
type ResultWindow =
  | { section: SearchMetadataKind; offset: number; limit: number }
  | { section: 'records'; offset: number; limit: number; total: number }

type WindowPage = SearchMetadataPageResponse | SearchRecordPageResponse

/** Every loaded window's rows, by their index in the list. */
interface WindowRows {
  metadata: Partial<Record<SearchMetadataKind, Record<number, SearchEntityHit>>>
  records: Record<number, Row>
}

const NO_WINDOWS: ResultWindow[] = []

interface SearchResultWindowsOptions {
  /** The section of the active result tab; null on the All tab. */
  section: Section | null
  /** The rows the tab renders; it changes on every scroll. */
  range: RowRange | null
  isPreview: boolean
  query: string
  matchParams: SearchMatchParams
  /** The search the windows belong to: a new one starts with none. */
  scope: string
  firstMetadataRows: Record<SearchMetadataKind, SearchEntityHit[]>
  metadataTotals: Record<SearchMetadataKind, number>
  firstRecordRows: Row[]
  recordTotal: number
}

/**
 * A result tab's rows beyond the search response, loaded as the list scrolls:
 * metadata windows at once, empty-search record batches once scrolling settles.
 * Returns the row at an index of the tab's list, or null while it loads.
 */
export function useSearchResultWindows({
  section,
  range,
  isPreview,
  query,
  matchParams,
  scope,
  firstMetadataRows,
  metadataTotals,
  firstRecordRows,
  recordTotal,
}: SearchResultWindowsOptions) {
  const [windows, setWindows] = useScopedState(scope, NO_WINDOWS)
  const combine = useCallback(
    (results: UseQueryResult<WindowPage>[]) => collectWindowRows(windows, results, query),
    [windows, query]
  )
  const windowRows = useQueries({
    queries: windows.map((window) => windowQuery(window, query, matchParams)),
    combine,
  })
  const requestWindow = useCallback(
    (window: ResultWindow) =>
      setWindows((current) =>
        current.some(
          (requested) =>
            requested.section === window.section &&
            requested.offset === window.offset &&
            requested.limit === window.limit
        )
          ? current
          : [...current, window]
      ),
    [setWindows]
  )

  const kind = section?.kind
  useEffect(() => {
    if (!kind || !range) return

    if (isMetadataKind(kind)) {
      const loaded = windowRows.metadata[kind]
      const window = planMetadataWindow(range, metadataTotals[kind], (index) =>
        Boolean(firstMetadataRows[kind][index] ?? loaded?.[index])
      )
      if (window) requestWindow({ section: kind, ...window })
      return
    }

    if (kind !== 'records' || !isPreview) return
    // Trailing-debounced: scrolling re-runs this on every position change, and
    // the fetch should only target where the viewport settles.
    const timer = setTimeout(() => {
      const offsets = planRecordBatches(range, recordTotal, (index) =>
        Boolean(firstRecordRows[index] ?? windowRows.records[index])
      )
      for (const offset of offsets) {
        requestWindow({
          section: 'records',
          offset,
          limit: RECORD_FETCH_BATCH_ROWS,
          total: recordTotal,
        })
      }
    }, RECORD_FETCH_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [
    kind,
    range,
    isPreview,
    metadataTotals,
    firstMetadataRows,
    firstRecordRows,
    recordTotal,
    windowRows,
    requestWindow,
  ])

  return useCallback(
    (tabSection: Section, index: number): Row | null => {
      if (isMetadataKind(tabSection.kind)) {
        const item =
          windowRows.metadata[tabSection.kind]?.[index] ?? firstMetadataRows[tabSection.kind][index]
        return item ? buildMetadataRow(tabSection.kind, item) : null
      }
      if (tabSection.kind === 'records' && isPreview) {
        return windowRows.records[index] ?? firstRecordRows[index] ?? null
      }
      return tabSection.rows[index] ?? null
    },
    [windowRows, firstMetadataRows, firstRecordRows, isPreview]
  )
}

function windowQuery(
  window: ResultWindow,
  query: string,
  matchParams: SearchMatchParams
): UseQueryOptions<WindowPage> {
  if (window.section === 'records') {
    const { offset, limit, total } = window
    return {
      queryKey: searchQueryKeys.previewRecordWindow(offset, limit, total),
      queryFn: () =>
        searchPreviewRecords(offset, limit, total).catch(
          logFailure('Failed to load virtual preview records')
        ),
      ...SEARCH_PAGE_QUERY_OPTIONS,
    }
  }
  const { section, offset, limit } = window
  return {
    queryKey: searchQueryKeys.globalSearchWindow(section, query, matchParams, offset, limit),
    queryFn: () =>
      searchMetadataPage(section, query, offset, limit, matchParams).catch(
        logFailure('Failed to load virtual search metadata')
      ),
    ...SEARCH_PAGE_QUERY_OPTIONS,
  }
}

function collectWindowRows(
  windows: ResultWindow[],
  results: UseQueryResult<WindowPage>[],
  query: string
): WindowRows {
  const rows: WindowRows = { metadata: {}, records: {} }
  results.forEach(({ data: page }, position) => {
    const window = windows[position]
    if (!window || !page) return
    if (window.section === 'records') {
      if (!('groups' in page)) return
      buildRecordRows(page.groups, query).forEach((row, index) => {
        rows.records[window.offset + index] = row
      })
      return
    }
    if (!('items' in page)) return
    const sectionRows = rows.metadata[window.section] ?? {}
    page.items.forEach((item, index) => {
      sectionRows[window.offset + index] = item
    })
    rows.metadata[window.section] = sectionRows
  })
  return rows
}
